import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from 'react';
import type { User } from '@/types';

import { AUTH_SERVICE_URL, sitzungErneuern } from './sitzung';

/**
 * Auth der Finance-App.
 *
 * Bewusst 1:1 der Flow aus `crm-web` — dieselben Endpunkte des zentralen
 * Auth-Service, dieselben Parameternamen. Ein selbst ausgedachter Pfad
 * (`/auth/google/login?redirect_uri=…`) fuehrt ins Leere; richtig ist
 * `/login/google?redirect_to=…`.
 *
 * Der Auth-Service setzt das HttpOnly-Cookie `__elbklar_access`, das domainweit
 * gilt: wer im CRM angemeldet ist, ist es hier auch.
 */

const API_URL = import.meta.env.VITE_API_URL || '/api';

const DEV_MODE = import.meta.env.VITE_DEV_AUTH === 'true';
const MOCK_USER: User = {
  id: 'dev-user-123',
  email: 'dev@elbklar.local',
  name: 'Dev User',
  role: 'admin',
  avatar_url: null,
  department: null,
  picture: null,
};

const SESSION_CHECK_INTERVAL = 60_000;

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  loginWithGoogle: (redirectTo?: string) => void;
  logout: () => Promise<void>;
  refreshSession: () => Promise<boolean>;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(() =>
    DEV_MODE ? MOCK_USER : null
  );
  const [isLoading, setIsLoading] = useState(!DEV_MODE);
  const [error, setError] = useState<string | null>(null);
  const sessionCheckRef = useRef<number | null>(null);

  /**
   * Sitzung auffrischen, über denselben Weg wie der API-Client (`sitzung.ts`):
   * Trifft die Sitzungsprüfung auf eine 401 der Benachrichtigungen, läuft nur
   * ein Refresh. Zwei parallele hätten das Refresh-Token zweimal rotiert.
   * (Der frühere Kommentar, der Aufruf sei cross-origin blockiert, stimmt
   * nicht mehr: core-api erlaubt finanzen.elbklar.com mit Credentials.)
   */
  const refreshSession = useCallback(async (): Promise<boolean> => (await sitzungErneuern()) === 'erneuert', []);

  /**
   * Sitzung lesen — bewusst ueber die EIGENE API.
   *
   * Der zentrale `/session` liegt auf `api.elbklar.com` und waere von
   * `finanzen.elbklar.com` aus ein Cross-Origin-Aufruf. Diese Domain steht
   * nicht in der CORS-Liste der core-api, der Browser wuerde ihn blockieren.
   * `/api/auth/me` laeuft dagegen same-origin ueber Caddy und liest dasselbe
   * Cookie.
   *
   * Der Login-Redirect selbst geht weiterhin an den zentralen Dienst — das ist
   * eine normale Navigation und von CORS nicht betroffen.
   *
   * `null`: nicht angemeldet. `undefined`: unklar (Netz oder Server gestört);
   * die laufende Sitzung bleibt dann stehen, statt auf die Anmeldung zu fallen.
   */
  const fetchSession = useCallback(async (): Promise<User | null | undefined> => {
    const lesen = async (response: Response): Promise<User> => {
      const data = await response.json();
      return {
        id: data.user_id,
        email: data.email,
        name: data.name,
        role: data.role,
        avatar_url: null,
        department: null,
        picture: null,
      };
    };
    try {
      const response = await fetch(`${API_URL}/auth/me`, { credentials: 'include' });
      if (response.ok) return lesen(response);
      if (response.status !== 401) return undefined;

      // 401 -> einmal erneuern (derselbe Refresh wie im API-Client) und erneut lesen.
      const erneuerung = await sitzungErneuern();
      if (erneuerung === 'nicht_erreichbar') return undefined;
      if (erneuerung === 'abgelehnt') return null;
      const retry = await fetch(`${API_URL}/auth/me`, { credentials: 'include' });
      if (retry.ok) return lesen(retry);
      return retry.status === 401 ? null : undefined;
    } catch {
      // Netz weg: Die Sitzung kann noch gelten; abmelden hülfe nicht.
      return undefined;
    }
  }, []);

  useEffect(() => {
    if (DEV_MODE) return;

    let mounted = true;

    const initAuth = async () => {
      const sessionUser = await fetchSession();
      if (mounted) {
        setUser(sessionUser ?? null);
        setIsLoading(false);
      }
    };

    void initAuth();

    sessionCheckRef.current = window.setInterval(async () => {
      const sessionUser = await fetchSession();
      if (sessionUser === null) setUser(null);
    }, SESSION_CHECK_INTERVAL);

    return () => {
      mounted = false;
      if (sessionCheckRef.current) window.clearInterval(sessionCheckRef.current);
    };
  }, [fetchSession]);

  /**
   * Weiterleitung zum zentralen Google-OAuth.
   *
   * Der Auth-Service braucht eine VOLLE URL zum Zurueckspringen — ein reiner
   * Pfad reicht nicht.
   */
  const loginWithGoogle = useCallback((redirectTo: string = '/') => {
    setError(null);
    const fullRedirectUrl = redirectTo.startsWith('http')
      ? redirectTo
      : `${window.location.origin}${redirectTo}`;
    window.location.href = `${AUTH_SERVICE_URL}/login/google?redirect_to=${encodeURIComponent(
      fullRedirectUrl
    )}`;
  }, []);

  const logout = useCallback(async () => {
    try {
      await fetch(`${AUTH_SERVICE_URL}/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // egal — lokal abmelden reicht
    }

    // Kein zweiter Abmelde-Aufruf an die eigene API: finance-api kennt nur
    // `GET /auth/me`, der fruehere `POST /auth/logout` lief immer auf 404
    // (Audit Oktober 2026, F2). Das Session-Cookie gehoert dem zentralen
    // Dienst, und dessen Logout oben ist der einzige, der etwas bewirkt.
    setUser(null);
    window.location.href = '/login';
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        loginWithGoogle,
        logout,
        refreshSession,
        error,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth muss innerhalb von AuthProvider verwendet werden');
  }
  return context;
}
